import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';

import { colors } from '@/assets/styles/colors';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { checkInLocationService } from '@/api/domains/checkInLocation';
import type { GeolocationRecord } from '@/api/domains/checkInLocation';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { coordinatesSchema } from '@/schemas/checkInLocation';


type GeolocalizacionNavigationState = {
  name?: string;
  address?: string | null;
  hasGeolocation?: boolean;
} | null;

export interface UseGeolocalizationPageResult {
  locationId: number;
  locationName: string;
  locationAddress: string | null;
  generateGeolocation: boolean;
  onToggleGenerateGeolocation: () => void;
  rows: GeolocationRecord[];
  columns: TableColumn<GeolocationRecord>[];
  actions: TableAction<GeolocationRecord>[];
  isLoading: boolean;
  latitude: string;
  longitude: string;
  onLatitudeChange: (value: string) => void;
  onLongitudeChange: (value: string) => void;
  onAdd: () => Promise<void>;
  onPreview: () => void;
  isAdding: boolean;
  radius: string;
  onRadiusChange: (value: string) => void;
  onGenerate: () => Promise<void>;
  isGenerating: boolean;
}

export function useGeolocalizationPage(): UseGeolocalizationPageResult {
  const { locationId: locationIdParam } = useParams<{ locationId: string }>();
  const { t: translate } = useTranslation('checkInLocation');
  const { showApiError, showSuccess, showNotification, openModal } = useUI();
  const { canDelete } = usePagePermissions();
  const routerLocation = useLocation();

  const navigationState = (routerLocation.state as GeolocalizacionNavigationState);

  const locationId = Number(locationIdParam ?? '0');

  const [rows, setRows] = useState<GeolocationRecord[]>([]);
  const [locationName, setLocationName] = useState(navigationState?.name ?? '');
  const [locationAddress, setLocationAddress] = useState<string | null>(
    navigationState?.address ?? null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [generateGeolocation, setGenerateGeolocation] = useState(
    navigationState?.hasGeolocation ?? false,
  );
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [radius, setRadius] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [mapUrl, setMapUrl] = useState<string | null>(null);

  useEffect(() => {
    if (Number.isNaN(locationId) || locationId === 0) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();

    const loadGeolocations = async () => {
      setIsLoading(true);
      const response = await checkInLocationService.getGeolocations(locationId, controller.signal);

      if (response.error?.code === 'ABORTED') return;

      if (!response.success || !response.data) {
        showApiError(response, translate('geolocalizacion.messages.loadError'));
        setIsLoading(false);
        return;
      }

      setLocationName(response.data.locationName);
      setLocationAddress(response.data.locationAddress);
      setMapUrl(response.data.mapUrl);
      setRows(response.data.records);
      setIsLoading(false);
    };

    void loadGeolocations();
    return () => { controller.abort(); };
  }, [locationId, showApiError, translate]);

  const refreshData = useCallback(async (): Promise<boolean> => {
    const response = await checkInLocationService.getGeolocations(
      locationId,
      new AbortController().signal,
    );
    if (response.success && response.data) {
      setRows(response.data.records);
      setMapUrl(response.data.mapUrl);
      return true;
    }
    return false;
  }, [locationId]);

  const refreshMapUrl = useCallback(async () => {
    const response = await checkInLocationService.getGeolocations(
      locationId,
      new AbortController().signal,
    );
    if (response.success && response.data) {
      setMapUrl(response.data.mapUrl);
    }
  }, [locationId]);

  const onToggleGenerateGeolocation = useCallback(() => {
    setGenerateGeolocation((previous) => !previous);
  }, []);

  const onAdd = useCallback(async () => {
    if (!latitude || !longitude) return;
    const parsed = coordinatesSchema.safeParse({ latitude, longitude });
    if (!parsed.success) {
      showNotification('error', translate('geolocalizacion.messages.invalidCoordinates'));
      return;
    }

    setIsAdding(true);
    try {
      const response = await checkInLocationService.createGeolocation(
        locationId,
        parsed.data.latitude,
        parsed.data.longitude,
      );
      if (response.success && response.data) {
        const newRecord = response.data;
        showSuccess(translate('geolocalizacion.messages.addSuccess'));
        setRows((previous) => [...previous, newRecord]);
        setLatitude('');
        setLongitude('');
        void refreshMapUrl();
      } else {
        showApiError(response, translate('geolocalizacion.messages.addError'));
      }
    } finally {
      setIsAdding(false);
    }
  }, [locationId, latitude, longitude, showApiError, showSuccess, showNotification, translate,
    refreshMapUrl]);

  const onGenerate = useCallback(async () => {
    const parsedRadius = Number(radius);
    if (!radius || Number.isNaN(parsedRadius) || parsedRadius <= 0) {
      showNotification('error', translate('geolocalizacion.messages.invalidRadius'));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await checkInLocationService.generateGeolocalizationPoints(
        locationId,
        parsedRadius,
      );
      if (response.success) {
        const refreshed = await refreshData();
        if (refreshed) {
          showSuccess(translate('geolocalizacion.messages.generateSuccess'));
        } else {
          showNotification('error', translate('geolocalizacion.messages.generateError'));
        }
      } else {
        showApiError(response, translate('geolocalizacion.messages.generateError'));
      }
    } finally {
      setIsGenerating(false);
    }
  }, [locationId, radius, showApiError, showSuccess, showNotification, translate, refreshData]);

  const onPreview = useCallback(() => {
    openModal('MapPreviewModal', {
      mapUrl,
      pointCount: rows.length,
      locationName,
      locationAddress,
      title: translate('geolocalizacion.previewModal.title'),
    });
  }, [mapUrl, rows.length, locationName, locationAddress, openModal, translate]);

  const handleDelete = useCallback((record: GeolocationRecord) => {
    openModal('ConfirmModal', {
      variant: 'error',
      icon: 'close',
      title: translate('geolocalizacion.modal.delete.title'),
      bodyText: translate('geolocalizacion.modal.delete.body'),
      confirmText: translate('geolocalizacion.modal.delete.confirm'),
      onConfirm: async () => {
        const response = await checkInLocationService.deleteGeolocation(locationId, record.id);
        if (response.success) {
          showSuccess(translate('geolocalizacion.messages.deleteSuccess'));
          setRows((previous) => previous.filter((item) => item.id !== record.id));
          void refreshMapUrl();
        } else {
          showApiError(response, translate('geolocalizacion.messages.deleteError'));
        }
      },
    });
  }, [locationId, openModal, translate, showSuccess, showApiError, refreshMapUrl]);

  const columns = useMemo<TableColumn<GeolocationRecord>[]>(() => [
    {
      key: 'provider',
      header: translate('geolocalizacion.table.columns.provider'),
      sortable: true,
      render: (row: GeolocationRecord) => row.provider || '—',
    },
    {
      key: 'latitude',
      header: translate('geolocalizacion.table.columns.latitude'),
      sortable: true,
      render: (row: GeolocationRecord) => String(row.latitude),
    },
    {
      key: 'longitude',
      header: translate('geolocalizacion.table.columns.longitude'),
      sortable: true,
      render: (row: GeolocationRecord) => String(row.longitude),
    },
  ], [translate]);

  const actions = useMemo<TableAction<GeolocationRecord>[]>(() => [
    {
      id: 'delete',
      label: translate('geolocalizacion.actions.delete'),
      icon: 'close',
      hidden: () => !canDelete,
      color: colors.feedback.error400,
      onClick: handleDelete,
    },
  ], [canDelete, translate, handleDelete]);

  return {
    locationId,
    locationName,
    locationAddress,
    generateGeolocation,
    onToggleGenerateGeolocation,
    rows,
    columns,
    actions,
    isLoading,
    latitude,
    longitude,
    onLatitudeChange: setLatitude,
    onLongitudeChange: setLongitude,
    onAdd,
    onPreview,
    isAdding,
    radius,
    onRadiusChange: setRadius,
    onGenerate,
    isGenerating,
  };
}
